import { create } from 'zustand'
import { devtools } from 'zustand/middleware'

export interface AudioState {
  currentSrc: string | null
  isPlaying: boolean
  isLoading: boolean
  currentTime: number
  duration: number
}

interface AudioActions {
  // Playback controls
  play: (src: string, startTime?: number) => void
  pause: () => void
  stop: () => void
  seek: (time: number) => void

  // Internal state updates (used by AudioPlayer component)
  setIsPlaying: (isPlaying: boolean) => void
  setIsLoading: (isLoading: boolean) => void
  setCurrentTime: (time: number) => void
  setDuration: (duration: number) => void
  setCurrentSrc: (src: string | null) => void

  // Cleanup
  cleanup: () => void
}

export type AudioStore = AudioState & AudioActions

export const useAudioStore = create<AudioStore>()(
  devtools(
    (set, get) => ({
      // Initial state
      currentSrc: null,
      isPlaying: false,
      isLoading: false,
      currentTime: 0,
      duration: 0,

      // Actions
      play: (src, startTime) => {
        const { currentSrc, isPlaying } = get()

        // If already playing this source, do nothing
        if (currentSrc === src && isPlaying) {
          return
        }

        set({
          currentSrc: src,
          isPlaying: true,
          isLoading: true,
          currentTime: startTime !== undefined ? startTime : 0,
          duration: 0,
        })
      },

      pause: () => {
        const { isPlaying } = get()
        if (isPlaying) {
          set({ isPlaying: false })
        }
      },

      stop: () => {
        set({
          currentSrc: null,
          isPlaying: false,
          currentTime: 0,
          duration: 0,
        })
      },

      seek: (time) => {
        const { duration } = get()

        // Clamp the time between 0 and duration
        const clampedTime = Math.max(0, Math.min(time, duration))

        set({ currentTime: clampedTime })
      },

      // Internal state updates
      setIsPlaying: (isPlaying) => set({ isPlaying }),
      setIsLoading: (isLoading) => set({ isLoading }),
      setCurrentTime: (currentTime) => set({ currentTime }),
      setDuration: (duration) => set({ duration }),
      setCurrentSrc: (currentSrc) => set({ currentSrc }),

      // Cleanup
      cleanup: () => {
        set({
          currentSrc: null,
          isPlaying: false,
          isLoading: false,
          currentTime: 0,
          duration: 0,
        })
      },
    }),
    { name: 'audio-store' }
  )
)
